import { Entypo, FontAwesome, MaterialIcons } from '@expo/vector-icons';
import { captureException } from '@sentry/react-native';
import { Image } from 'expo-image';
import * as ImagePicker from 'expo-image-picker';
import { useEffect, useState } from 'react';
import { Control, FieldValues, Path, useController } from 'react-hook-form';
import { Linking, StyleSheet, TouchableOpacity, View } from 'react-native';
import { Modal, Portal } from 'react-native-paper';

import { useEndFlowModal } from '@/contexts/endFlowModalContext';
import { i18n } from '@/utils/i18n';

import BrandedButton from './buttons/BrandedButton';
import CloseButton from './buttons/CloseButton';
import Text from './Text';

export type ProfilePicPickerFIeldProps<T extends FieldValues> = {
  control: Control<T>;
  name: Path<T>;
  showError?: boolean;
};

const ProfilePicPickerField = <T extends FieldValues>({
  name,
  control,
  showError = false
}: ProfilePicPickerFIeldProps<T>) => {
  const [showTypePicker, setShowTypePicker] = useState(false);
  const [imageFile, setImageFile] = useState<string | undefined>(undefined);

  const { showError: errorModal } = useEndFlowModal();

  const {
    field: { onChange, value },
    fieldState: { error }
  } = useController<T>({
    name,
    control
  });

  //Locales
  const edit = i18n.t('mix.edit_title');

  const setImage = (uri: string) => {
    onChange(uri);
    setImageFile(uri);
  };

  const launchCamera = async () => {
    let result = await ImagePicker.launchCameraAsync({
      mediaTypes: 'images',
      allowsEditing: true,
      aspect: [4, 4]
    });
    if (!result.canceled) setImage(result.assets[0].uri);
  };

  const pickImageFromLibrary = async () => {
    // No permissions request is necessary for launching the image library
    let result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: 'images',
      allowsEditing: true,
      aspect: [4, 4]
    });

    if (!result.canceled) setImage(result.assets[0].uri);
  };

  const pickImageFromCamera = async () => {
    try {
      const permissions = await ImagePicker.getCameraPermissionsAsync();

      if (!permissions?.granted && permissions?.canAskAgain) {
        ImagePicker.requestCameraPermissionsAsync().then(async (firstPermissions) => {
          if (firstPermissions.granted) launchCamera();
        });
      } else if (permissions?.granted) {
        launchCamera();
      } else {
        Linking.openSettings();
      }
    } catch (e: any) {
      captureException(e);
      errorModal({ title: 'Error taking the picture!', message: e.message });
    }
  };

  useEffect(() => {
    if (value) setImage(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <View style={styles.container}>
      <Portal>
        <Modal
          style={{ alignItems: 'center' }}
          visible={showTypePicker}
          onDismiss={() => setShowTypePicker(false)}
          contentContainerStyle={styles.modal}>
          <View style={{ paddingHorizontal: 10, flexDirection: 'column' }}>
            <CloseButton
              style={styles.closeButton}
              onCloseTap={() => {
                setShowTypePicker(false);
              }}
            />
            <View style={{ flexDirection: 'column' }}>
              <Text fontWeight="900" style={styles.selectTypeHeader}>
                {i18n.t('mix.choose_from')}
              </Text>
              <View style={styles.buttonsContainer}>
                <BrandedButton
                  style={[styles.flexOne, { paddingHorizontal: 16 }]}
                  textStyles={{ overflow: 'visible' }}
                  textProps={{ numberOfLines: 1 }}
                  onPress={() => {
                    setShowTypePicker(false);
                    pickImageFromLibrary();
                  }}>
                  <MaterialIcons name="add-photo-alternate" color="white" />
                  {` ${i18n.t('mix.gallery')}`}
                </BrandedButton>

                <BrandedButton
                  style={[styles.flexOne, { paddingHorizontal: 16 }]}
                  textStyles={{ overflow: 'visible' }}
                  textProps={{ numberOfLines: 1 }}
                  onPress={() => {
                    setShowTypePicker(false);
                    pickImageFromCamera();
                  }}>
                  <MaterialIcons name="add-a-photo" color="white" />
                  {` ${i18n.t('mix.camera')}`}
                </BrandedButton>
              </View>
            </View>
          </View>
        </Modal>
      </Portal>
      {imageFile ? (
        <Image source={imageFile} style={styles.profilePic} />
      ) : (
        <FontAwesome name="user-circle-o" size={112} color="black" />
      )}
      <TouchableOpacity
        onPress={() => {
          setShowTypePicker(true);
        }}
        style={styles.editBtnContainer}>
        <Entypo name="camera" size={20} color="black" />
        <Text style={styles.editBtnLabel} fontWeight="700">
          {edit}
        </Text>
      </TouchableOpacity>
      {showError && !!error?.message && <Text style={styles.errorText}>{error.message}</Text>}
    </View>
  );
};

const styles = StyleSheet.create({
  modal: {
    backgroundColor: 'white',
    paddingHorizontal: 10,
    paddingVertical: 25,
    marginHorizontal: 18
  },

  closeButton: {
    width: '100%',
    alignItems: 'flex-start'
  },

  selectTypeHeader: {
    fontSize: 22,
    color: 'black',
    justifyContent: 'center',
    alignSelf: 'center',
    textAlign: 'center',
    marginBottom: 20
  },

  container: {
    flexDirection: 'column',
    alignItems: 'center'
  },

  profilePic: {
    width: 112,
    height: 112,
    borderRadius: 56
  },

  editBtnContainer: {
    backgroundColor: 'white',
    paddingHorizontal: 20,
    paddingVertical: 7,
    flexDirection: 'row',
    alignItems: 'center',
    columnGap: 5,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: '#D5D5D5',
    alignSelf: 'center',
    position: 'absolute',
    bottom: -15
  },

  editBtnLabel: {
    color: 'black',
    fontSize: 16
  },

  flexOne: {
    flex: 1
  },

  buttonsContainer: {
    flexDirection: 'row',
    gap: 15
  },

  errorText: {
    marginTop: 10,
    fontSize: 12,
    color: 'red'
  }
});

export default ProfilePicPickerField;
